export type CrmType = 'HUBSPOT' | 'SALESFORCE';
type CrmEntityType = 'contact' | 'lead' | 'company' | 'list';

interface CrmUrlOptions {
  hubspotPortalId?: string;
  salesforceInstanceUrl?: string;
}

/**
 * Get CRM URL for contact, company, or list.
 * Routes to the appropriate CRM-specific function.
 */
export function getCrmUrl(
  crmType: CrmType,
  entityType: CrmEntityType,
  entityId: string | undefined,
  options?: CrmUrlOptions
): string | null {
  if (!entityId) return null;

  if (crmType === 'HUBSPOT') {
    return getHubspotUrl(options?.hubspotPortalId, entityType, entityId);
  } else {
    return getSalesforceUrl(options?.salesforceInstanceUrl, entityType, entityId);
  }
}

/**
 * HubSpot-specific URL builder.
 * Returns null if portalId or entityId is missing.
 */
export function getHubspotUrl(
  portalId: string | undefined,
  entityType: CrmEntityType,
  entityId: string | undefined
): string | null {
  if (!portalId || !entityId) return null;

  switch (entityType) {
    case 'contact':
      return `https://app.hubspot.com/contacts/${portalId}/contact/${entityId}`;
    case 'company':
      return `https://app.hubspot.com/contacts/${portalId}/company/${entityId}`;
    case 'list':
      return `https://app.hubspot.com/contacts/${portalId}/objectLists/${entityId}/filters`;
    default:
      return null;
  }
}

/**
 * Salesforce-specific URL builder.
 * Returns null if instanceUrl or entityId is missing.
 * Uses Lightning Experience URL format: /lightning/r/{Object}/{id}/view
 */
function getSalesforceUrl(
  instanceUrl: string | undefined,
  entityType: CrmEntityType,
  entityId: string | undefined
): string | null {
  if (!instanceUrl || !entityId) return null;

  // Map entity type to Salesforce object name
  let objectName: string;
  switch (entityType) {
    case 'contact':
      objectName = 'Contact';
      break;
    case 'lead':
      objectName = 'Lead';
      break;
    case 'company':
      objectName = 'Account';
      break;
    default:
      objectName = 'Account';
  }

  // Use Lightning Experience URL format
  return `${instanceUrl}/lightning/r/${objectName}/${entityId}/view`;
}
